import React from 'react';
import { RegionalFreePeriodEligibility } from '../../api/Api';
import styles from './RegionalFreePeriodBanner.module.scss';

interface Props {
    eligibility: RegionalFreePeriodEligibility;
}

/** e.g. same year → "August 1 to September 30, 2026"; cross-year → year on both. */
export const formatFreePeriodDateRange = (startDate: string, endDate: string): string => {
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return `${startDate} to ${endDate}`;
    }
    const startMonthDay = start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    const endMonthDay = end.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    const endWithYear = end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    if (start.getFullYear() === end.getFullYear()) {
        return `${startMonthDay} to ${endWithYear}`;
    }
    const startWithYear = start.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    return `${startWithYear} to ${endWithYear}`;
};

export const ELIGIBLE_PLACEMENT_HEADLINE = 'Good news: this shift qualifies!';

export const buildEligiblePlacementMessage = (
    province: string,
    startDate: string,
    endDate: string,
): string => (
    `As a thank you to ${province}'s amazing dental community, we are waiving placement fees (normally $73 +tax) for shifts dated ${formatFreePeriodDateRange(startDate, endDate)}.`
);

const ELIGIBLE_NOTE = 'No placement fee will be charged after this completed shift!';

const RegionalFreePeriodBanner = ({ eligibility }: Props) => {
    if (!eligibility.campaignId || !eligibility.customerFacingMessage) return null;

    const isEligiblePlacement = eligibility.eligible
        && Boolean(eligibility.province)
        && Boolean(eligibility.startDate)
        && Boolean(eligibility.endDate);

    const title = isEligiblePlacement
        ? ELIGIBLE_PLACEMENT_HEADLINE
        : eligibility.campaignName;

    const message = isEligiblePlacement
        ? buildEligiblePlacementMessage(
            eligibility.province,
            eligibility.startDate as string,
            eligibility.endDate as string,
        )
        : eligibility.customerFacingMessage;

    return (
        <section
            className={styles.banner}
            role="status"
            aria-live="polite"
            data-testid="regional-free-period-banner"
        >
            <span className={styles.icon} aria-hidden="true">✓</span>
            <div>
                <p className={styles.title}>{title}</p>
                <p className={styles.message}>{message}</p>
                {eligibility.eligible && (
                    <p className={styles.note}>{ELIGIBLE_NOTE}</p>
                )}
            </div>
        </section>
    );
};

export default RegionalFreePeriodBanner;
